import { useState, useRef, useLayoutEffect } from 'react';
import { ChevronRight } from 'lucide-react';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';
import { courseWordForm } from '../../data/coursePresentation';

const CurriculumTab = ({ module, isOpen, onToggle, tabNum }) => {
  const panelId = `curriculum-module-${tabNum}`;
  const contentRef = useRef(null);
  const [contentHeight, setContentHeight] = useState(0);
  const lessons = module.lessons || [];
  useLayoutEffect(() => {
    const content = contentRef.current;
    const measure = () => setContentHeight(content.getBoundingClientRect().height);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(content);
    return () => observer.disconnect();
  }, []);
  return <div className="curriculum-module">
    <h3><button id={`${panelId}-toggle`} type="button" className="curriculum-toggle" aria-expanded={isOpen} aria-controls={panelId} onClick={onToggle}>
      <span className="curriculum-number">{String(tabNum).padStart(2, '0')}</span>
      <span>{module.title}<small>{module.info || `${lessons.length} ${courseWordForm(lessons.length, ['урок','урока','уроков'])}`}</small></span>
      <ChevronRight aria-hidden="true" size={20} className="curriculum-chevron"/>
    </button></h3>
    <div id={panelId} aria-labelledby={`${panelId}-toggle`} aria-hidden={!isOpen} inert={!isOpen}
      className="curriculum-panel" style={{ height: isOpen ? contentHeight : 0 }}>
    <div ref={contentRef} className="curriculum-lessons">
      {lessons.map((lesson, idx) => <div key={idx} className="curriculum-lesson">
        <span className="curriculum-number">{String(idx + 1).padStart(2, '0')}</span>
        <span>{typeof lesson === 'string' ? lesson : lesson.lesson}</span>
        {lesson.time && <span className="curriculum-time">{lesson.time}</span>}
      </div>)}
    </div>
    </div>
  </div>;
};

export const CurriculumSection = ({ curriculum, courseImage, courseTitle, courseDescription, noteTitle = 'От темы к практике' }) => {
  const [headingRef, isVisible] = useScrollAnimation();
  // Use positional keys so both curriculum formats and missing IDs behave consistently.
  const [openTabs, setOpenTabs] = useState([0]);
  const [animateChanges, setAnimateChanges] = useState(true);
  const modules = curriculum?.modules || (Array.isArray(curriculum) ? curriculum : []);
  if (!modules.length) return null;
  const allOpen = openTabs.length === modules.length;
  const toggle = (index, event) => {
    setAnimateChanges(event.detail > 0);
    setOpenTabs(prev => prev.includes(index) ? prev.filter(i => i !== index) : [...prev, index]);
  };
  const toggleAll = event => {
    setAnimateChanges(event.detail > 0);
    setOpenTabs(allOpen ? [] : modules.map((_, index) => index));
  };
  return <section className="editorial-section" id="course-program" data-testid="curriculum-section" data-motion={animateChanges ? 'smooth' : 'instant'}><div className="editorial-container">
    <div ref={headingRef} className={`course-section-reveal ${isVisible ? 'is-visible' : ''}`}><p className="course-eyebrow">Что внутри</p><h2>Программа <span className="ink-underline">курса</span></h2></div>
    <div className="course-curriculum-sheet">
      <div className="course-curriculum-main"><div className="course-program-controls"><span>{modules.length} {courseWordForm(modules.length, ['раздел','раздела','разделов'])}</span><button type="button" onClick={toggleAll} aria-expanded={allOpen} aria-controls="course-module-list">{allOpen ? 'Свернуть все разделы' : 'Раскрыть все разделы'}</button></div>
        <div id="course-module-list">{modules.map((module, index) => <CurriculumTab key={index} module={module} tabNum={index + 1} isOpen={openTabs.includes(index)} onToggle={event => toggle(index, event)}/>)}</div>
      </div>
      <aside className="course-curriculum-note"><img src={courseImage} alt="" width="960" height="540" loading="lazy"/><p className="course-eyebrow">Fokeev / {courseTitle}</p><h3>{noteTitle}</h3><p>{courseDescription}</p></aside>
    </div>
  </div></section>;
};
